import { COPY } from './const';
import type { TPoemState } from './client/types';

type TErrorState = Extract<TPoemState, { phase: 'error' }>;

function limitText(limit?: string): string {
    switch (limit) {
        case 'daily':
            return COPY.limitDaily;
        case 'ip':
        case 'site':
            return COPY.limitBusy;
        default: // hourly, dup, unknown
            return COPY.limitHourly;
    }
}

/**
 * What went wrong, in words. Every failure then opens the stanza fields for the
 * person to write the poem themselves: a card on this template cannot be created
 * without one, and a broken or spent generator must not end the sale.
 */
export function failureText(state: TErrorState): string {
    switch (state.reason) {
        case 'limit':
            return limitText(state.limit);
        case 'failed':
            return `${COPY.failed} ${COPY.tryAgain}`;
        case 'timeout':
            return `${COPY.timeout} ${COPY.tryAgain}`;
        case 'input':
            return state.field === 'prompt' ? COPY.badPrompt : COPY.badName;
        case 'busy':
            return COPY.busy;
        case 'mismatch':
            return `${COPY.mismatch} ${COPY.tryAgain}`;
        case 'off':
            return COPY.off;
        case 'rejected':
            return COPY.rejected;
        default:
            return COPY.unavailable;
    }
}

/**
 * Failures only the browser knows about go to Sentry: the server counts its own
 * (a failed job, a limit, an unavailable queue it answered 503 to), but not a
 * request that never reached it, a poll it never saw, or a form it refused.
 * A switched-off poem, a poem busy elsewhere and a refused input have a known
 * cause and say it in words.
 */
export function isReportable(state: TErrorState): boolean {
    return (
        state.reason === 'timeout' ||
        state.reason === 'rejected' ||
        state.reason === 'mismatch' ||
        (state.reason === 'unavailable' && state.retryAfter === undefined)
    );
}

/** The inputs a poem was written from, to notice a later change. */
export function inputsKey(prompt: string, toName: string, fromName: string): string {
    return JSON.stringify([prompt.trim(), toName.trim(), fromName.trim()]);
}

/**
 * Whether replacing the stanzas loses someone's work: a stanza differs from the
 * last generated poem, or there is text and no generated poem to compare with
 * (a saved card's poem, or one written by hand).
 */
export function hasOwnText(current: string[], generated: string[] | null): boolean {
    if (!generated) {
        return current.some((text) => text.trim() !== '');
    }

    return current.some((text, i) => text.trim() !== (generated[i] ?? '').trim());
}

/**
 * What to ask before a new poem replaces the stanzas, null = nothing to lose.
 * A saved card's poem was not edited here: asking "you edited it" would be wrong.
 */
export function replaceQuestion(
    current: string[],
    generated: string[] | null
): string | null {
    if (!hasOwnText(current, generated)) {
        return null;
    }

    return generated ? COPY.confirmReplaceEdited : COPY.confirmReplaceSaved;
}

/** The stanzas are exactly this form's slots: no field left out, none extra. */
export function matchesFields(
    fields: Array<{ dataset: DOMStringMap }>,
    textById: Map<number, string>
): boolean {
    return (
        fields.length === textById.size &&
        fields.every((field) => textById.has(Number(field.dataset.id)))
    );
}

/**
 * A stanza the save keeps: something left once emoji go. The server strips them
 * (StringHelper::filterEmoji plus the variation selectors and joiner,
 * Tool\Card\SubtitleTexts), so a stanza of emoji alone would come out empty;
 * "…" or "!!!" stays, as it does there.
 */
export function hasTextBeyondEmoji(text: string): boolean {
    return (
        text
            // a keycap ("1️⃣") goes whole there, digit included
            .replace(/[#*0-9]\u{FE0F}?\u{20E3}/gu, '')
            .replace(
                /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\u{FE0E}\u{FE0F}\u{200D}\u{20E3}]/gu,
                ''
            )
            .trim() !== ''
    );
}

/** Every stanza fits its field's maxlength (both count UTF-16, as the server). */
export function fitsFields(
    fields: Array<{ maxLength: number; dataset: DOMStringMap }>,
    textById: Map<number, string>
): boolean {
    return fields.every((field) => {
        const text = textById.get(Number(field.dataset.id)) ?? '';

        return field.maxLength <= 0 || text.length <= field.maxLength;
    });
}

export type TPoemOrigin = 'generated' | 'edited' | 'manual';

/** How the poem of the card being created came about, for `card-created`. */
export function poemOrigin(
    current: string[],
    generated: string[] | null
): TPoemOrigin | null {
    if (current.every((text) => text.trim() === '')) {
        return null;
    }
    if (!generated) {
        return 'manual';
    }

    return hasOwnText(current, generated) ? 'edited' : 'generated';
}
